import React, { useState, useEffect } from 'react';
import { Modal, Form, App } from 'antd';
import type { PartyMember, Image } from '../../types/types';
import styles from './CharacterSheetModal.module.css';
import { logger } from '../../services/logger.client';
import { useRenderLoopDetection } from '../../hooks/hookUtils';
import CharacterSheetHeader from './CharacterSheetHeader';
import CharacterSheetStats from './CharacterSheetStats';
import CharacterSheetTabs from './CharacterSheetTabs';

interface CharacterSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  player: PartyMember | null;
  imageLibrary: Image[];
  onUpdatePlayer: (player: Partial<PartyMember> & { id: number }) => Promise<void>;
}

const CharacterSheetModal: React.FC<CharacterSheetModalProps> = ({
  isOpen,
  onClose,
  player,
  imageLibrary,
  onUpdatePlayer,
}) => {
  useRenderLoopDetection('CharacterSheetModal');
  logger.info({ player: player?.name, isOpen }, 'CharacterSheetModal: Rendering.');
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);
  const { message } = App.useApp();

  // Local state for complex, editable lists
  const [inventory, setInventory] = useState(player?.inventory ?? []);
  const [features, setFeatures] = useState(player?.features ?? []);
  const [spells, setSpells] = useState(player?.spells ?? []);

  // This effect runs when the player prop changes, to update the local state for the lists.
  useEffect(() => {
    logger.info({ player: player?.name, isOpen }, 'CharacterSheetModal: useEffect running.');
    if (player) {
      setInventory(player.inventory || []);
      setFeatures(player.features || []);
      setSpells(player.spells || []);
      // Set form values directly here. The form will be re-keyed, but this ensures it's up-to-date.
      form.setFieldsValue(player);
    }
  }, [player, form, isOpen]);

  const handleSave = async () => {
    if (!player) return;

    try {
      setIsSaving(true);
      const values = await form.validateFields();
      // The form gives us a nested `stats` object, but the API expects a flat structure.
      // We need to flatten it.
      const { stats, ...otherValues } = values;
      const flatStats = {
        statStr: stats.str, statDex: stats.dex, statCon: stats.con,
        statInt: stats.int, statWis: stats.wis, statCha: stats.cha,
      };
      const updatedPlayer = {
        ...player,
        ...otherValues,
        ...flatStats,
        inventory,
        features,
        spells,
      };
      await onUpdatePlayer(updatedPlayer); // The container's callback will send this to the backend
      message.success(`${player.name}'s sheet has been updated.`);
      onClose();
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'info' in error) { // This is an Antd form validation error
        message.error('Please fill out all required fields.');
      } else if (error instanceof Error) { // This is likely an error from the API call
        message.error(`Failed to save changes: ${error.message}`);
      } else {
        message.error('An unknown error occurred while saving.');
      }
      logger.error({ error, player }, 'Character sheet validation failed.');
    } finally {
      setIsSaving(false);
    }
  };

  const playerImage = player?.imageId ? imageLibrary.find((img: Image) => img.id === player.imageId) : null;

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      onOk={handleSave}
      okText="Save Changes"
      confirmLoading={isSaving}
      width={800}
      // The title is now part of the Form, so we just pass the text.
      title="Edit Character Sheet"
      destroyOnHidden // Use destroyOnHidden to unmount contents when modal is closed
    >
      <Form form={form} key={player?.id ?? 'new'} layout="vertical" initialValues={player ?? { stats: { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 } }}>
        <div className={styles.body}>
          <div className={styles.mainInfo}>
            <CharacterSheetHeader player={player} playerImage={playerImage} form={form} />
            <CharacterSheetStats />
          </div>
          <div className={styles.detailsSection}>
            <CharacterSheetTabs
              inventory={inventory}
              setInventory={setInventory}
              features={features}
              setFeatures={setFeatures}
              spells={spells}
              setSpells={setSpells}
            />
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default CharacterSheetModal;